import { valueAt } from "../common/valueAt";
import type { AlgorithmOperation, OperationType } from "@algoquest/schemas";

/**
 * Selection Sort — framework-independent Algorithm Engine (Master Spec
 * §8, §13, §24, §26). Values are visible from the start (§24), same as
 * Bubble Sort. Gameplay identity per §26 is "scan + track minimum/maximum
 * + place": for each round, the engine assumes the first element of the
 * remaining unsorted region is the initial candidate, then the player
 * scans every remaining element one at a time, deciding at each one
 * whether it "select"s as the new best-so-far candidate or is "skip"ped
 * — "the player actively identifies the candidate" (§26), it is never
 * inferred automatically. Once the scan reaches the end of the region,
 * the engine places the final candidate into position (swapping it there
 * if it isn't already first) and the unsorted region shrinks by one from
 * the left.
 *
 * Two-phase interaction per scan step, mirroring Bubble Sort's and Binary
 * Search's shape: inspect() reveals the current scanIndex-vs-candidate
 * pair as a COMPARE event; decide() commits to "select" or "skip",
 * validated against the engine's own (never-exposed) comparison result —
 * a wrong commitment increments `mistakes` and leaves state unchanged,
 * forcing a retry. Placement at the end of a round is automatic (there is
 * no "wrong way" to place a known candidate), exactly like Bubble Sort's
 * PASS_COMPLETE transition needing no separate player action.
 */
export interface SelectionSortDomainState {
  values: number[];
  sortDirection: "ascending" | "descending";
  unsortedStart: number;
  scanIndex: number;
  currentBestIndex: number;
  awaitingDecision: boolean;
  sortedIndices: number[];
  comparisons: number;
  swaps: number;
  passesCompleted: number;
  mistakes: number;
  isComplete: boolean;
}

export interface SelectionSortStepResult {
  operations: AlgorithmOperation[];
  state: SelectionSortDomainState;
}

export interface SelectionSortDecisionResult {
  wasCorrect: boolean;
  result: SelectionSortStepResult | null;
}

export class SelectionSortEngine {
  private readonly values: number[];
  private readonly sortDirection: "ascending" | "descending";
  private unsortedStart = 0;
  private scanIndex: number;
  private currentBestIndex = 0;
  private awaitingDecision = false;
  private sortedIndices: number[] = [];
  private comparisons = 0;
  private swaps = 0;
  private passesCompleted = 0;
  private mistakes = 0;
  private isComplete = false;
  private sequenceCounter = 0;
  private readonly trace: AlgorithmOperation[] = [];

  constructor(values: number[], sortDirection: "ascending" | "descending") {
    if (values.length < 2) {
      throw new Error("SelectionSortEngine requires at least 2 elements");
    }
    this.values = [...values];
    this.sortDirection = sortDirection;
    this.scanIndex = 1;
  }

  getState(): SelectionSortDomainState {
    return {
      values: [...this.values],
      sortDirection: this.sortDirection,
      unsortedStart: this.unsortedStart,
      scanIndex: this.scanIndex,
      currentBestIndex: this.currentBestIndex,
      awaitingDecision: this.awaitingDecision,
      sortedIndices: [...this.sortedIndices].sort((a, b) => a - b),
      comparisons: this.comparisons,
      swaps: this.swaps,
      passesCompleted: this.passesCompleted,
      mistakes: this.mistakes,
      isComplete: this.isComplete,
    };
  }

  getTrace(): AlgorithmOperation[] {
    return [...this.trace];
  }

  /** Reveals the current candidate-vs-scan pair as a COMPARE event. */
  inspect(nowMs: number): SelectionSortStepResult | null {
    if (this.isComplete || this.awaitingDecision) {
      return null;
    }
    const candidate = this.currentBestIndex;
    const scan = this.scanIndex;
    this.comparisons += 1;
    const operations: AlgorithmOperation[] = [
      this.emit("COMPARE", nowMs, {
        indices: [candidate, scan],
        values: [valueAt(this.values, candidate), valueAt(this.values, scan)],
      }),
    ];
    this.awaitingDecision = true;
    return { operations, state: this.getState() };
  }

  /** The player's "select" (this is the new best) or "skip" (keep current best) commitment. */
  decide(choice: "select" | "skip", nowMs: number): SelectionSortDecisionResult {
    if (this.isComplete || !this.awaitingDecision) {
      return { wasCorrect: false, result: null };
    }
    const isBetter = this.isBetterCandidate();
    const correctChoice: "select" | "skip" = isBetter ? "select" : "skip";
    if (choice !== correctChoice) {
      this.mistakes += 1;
      return { wasCorrect: false, result: null };
    }

    const scan = this.scanIndex;
    const operations: AlgorithmOperation[] = [];

    if (choice === "select") {
      this.currentBestIndex = scan;
      const selectType: OperationType = this.sortDirection === "ascending" ? "SELECT_MIN" : "SELECT_MAX";
      operations.push(this.emit(selectType, nowMs, { indices: [scan], values: [valueAt(this.values, scan)] }));
    } else {
      operations.push(
        this.emit("KEEP", nowMs, { indices: [scan, this.currentBestIndex], values: [valueAt(this.values, scan), valueAt(this.values, this.currentBestIndex)] }),
      );
    }

    this.awaitingDecision = false;
    this.scanIndex += 1;

    if (this.scanIndex >= this.values.length) {
      const start = this.unsortedStart;
      const best = this.currentBestIndex;
      this.passesCompleted += 1;
      if (best !== start) {
        const startValue = valueAt(this.values, start);
        const bestValue = valueAt(this.values, best);
        this.values[start] = bestValue;
        this.values[best] = startValue;
        this.swaps += 1;
        operations.push(this.emit("PLACE", nowMs, { indices: [start, best], values: [bestValue, startValue] }));
      } else {
        operations.push(this.emit("PLACE", nowMs, { indices: [start], values: [valueAt(this.values, start)] }));
      }
      this.sortedIndices.push(start);
      this.unsortedStart += 1;

      if (this.unsortedStart >= this.values.length - 1) {
        this.isComplete = true;
        for (let i = this.unsortedStart; i < this.values.length; i += 1) {
          if (!this.sortedIndices.includes(i)) {
            this.sortedIndices.push(i);
          }
        }
      } else {
        this.currentBestIndex = this.unsortedStart;
        this.scanIndex = this.unsortedStart + 1;
      }
    }

    return { wasCorrect: true, result: { operations, state: this.getState() } };
  }

  private isBetterCandidate(): boolean {
    const scanValue = valueAt(this.values, this.scanIndex);
    const bestValue = valueAt(this.values, this.currentBestIndex);
    return this.sortDirection === "ascending" ? scanValue < bestValue : scanValue > bestValue;
  }

  private emit(
    type: OperationType,
    timestampMs: number,
    partial: Partial<Omit<AlgorithmOperation, "sequence" | "type" | "timestampMs">>,
  ): AlgorithmOperation {
    const operation: AlgorithmOperation = {
      sequence: this.sequenceCounter,
      type,
      timestampMs,
      ...partial,
    };
    this.sequenceCounter += 1;
    this.trace.push(operation);
    return operation;
  }
}
